import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ArrowLeft from '../../Assets/Arrow left.svg';
import CouponName from './Components/CouponName';
import AddCouponImage from './Components/AddCouponImage';
import SelectCollectionOrProduct from './Components/SelectCollectionOrProduct';
import PurchaseRequirement from './Components/PurchaseRequirement';
import CouponLimitation from './Components/CouponLimitation';
import AddDuration from './Components/AddDuration';
import CropImage from '../../Components/CropImage/CropImage';
import Modal from '../CouponManagement/Components/Modals/Modal';
import { CropImageContext } from '../../Context/CropImageContext';
import { AddCouponFormContext } from '../../Context/Coupons/AddCouponFormContext';
import SetFixedPrice from './Components/SetFixedPrice';
import imageCompression from 'browser-image-compression';
import { formatDateToMMDDYYYY, getMediaUrl } from '../../lib/utils/utils';
import { showToast } from '../../Components/Toaster/Toaster';
import { createCoupon } from '../../lib/utils/apiCalls';

function AddFixedDiscount() {
    const navigate = useNavigate();
    const location = useLocation();

    const { modalOpen } = useContext(CropImageContext);

    const {
        formInputs,
        image,
        setFormErrors,
        defaultFormErrors,
        date,
        purchaseRequirement,
        limitType,
        selectedProducts,
        selectedCollection,
        selectedCustomCollection
    } = useContext(AddCouponFormContext);

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        if(modalOpen) {
            setModalVisible(true);
        } else {
            setModalVisible(false);
        }
    }, [modalOpen]);

    const handlePublishCoupon = async () => {
        const validationErrors = {};

        if(!formInputs.name.trim()) {
            validationErrors.name = 'Required field';
        }

        if(!formInputs.fixedPrice.trim()) {
            validationErrors.fixedPrice = 'Required field';
        }

        if(Object.keys(image).length === 0) {
            validationErrors.image = 'Image is required';
        }

        if((Object.keys(selectedCollection).length === 0 && Object.keys(selectedCustomCollection).length === 0 && selectedProducts.length === 0)) {
            validationErrors.collectionOrProduct = 'Collection or product is required';
        }

        if (!purchaseRequirement) {
            validationErrors.purchaseAmount = 'Select an option';
        }
        else if (purchaseRequirement === 'purchase-required') {
            if (!formInputs.purchaseAmount) {
                validationErrors.purchaseAmount = 'Purchase amount is required';
            }
            else if (formInputs.fixedPrice && (Number(formInputs.purchaseAmount) < Number(formInputs.fixedPrice))) {
                validationErrors.fixedPrice = 'Discount can\'t be greater than purchase amount.';
            }
        }

        if(!limitType) {
            validationErrors.limitAmount = 'Select an option';
        } else if (limitType !== "no-limit") {
            if (!formInputs.limitAmount) {
                validationErrors.limitAmount = "Limit amount is required";
            }
        }

        if(!date.from && !date.to) {
            validationErrors.date = 'Duration is required';
        }

        if(Object.keys(validationErrors).length === 0) {
            setFormErrors(defaultFormErrors);

            try {
                let url;

                if(image.file.size > 1048576) {
                    try {
                        let compressedBlob = await imageCompression(image.file, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1920,
                            useWebWorker: true
                        });
    
                        let compressedFile = new File([compressedBlob], image.file.name, { type: image.file.type, lastModified: Date.now() });
                    
                        if (compressedFile) {
                            url = await getMediaUrl(compressedFile);
                        }
                        else {
                            url = await getMediaUrl(image.file);
                        }
                    }
                    catch(err) {
                        url = await getMediaUrl(image.file);
                    }
                }
                else {
                    url = await getMediaUrl(image.file);
                }

                const callData = {
                    pic: url,
                    typeCoupon: "FIXED_DISCOUNTS",
                    couponName: formInputs.name,
                    limitUserBoolean: limitType === 'limit-order-per-customer' ? true : false,
                    limitNumber: Number(formInputs.limitAmount),
                    discountMoney: formInputs.fixedPrice,
                    startDate: formatDateToMMDDYYYY(date.from),
                    visibleDate: formatDateToMMDDYYYY(date.from),
                    endDate: formatDateToMMDDYYYY(date.to),
                    isVisible: true,
                }

                if(purchaseRequirement === 'purchase-required') {
                    callData.purchaseRequired = true;
                    callData.purchaseValue = formInputs.purchaseAmount;
                }
                else {
                    callData.purchaseRequired = false;
                }

                if(Object.keys(selectedCollection).length > 0) {
                    callData.selectTypeCoupon = 'CATEGORY';
                    callData.selectCollectionId = selectedCollection.id;
                }
                else if(Object.keys(selectedCustomCollection).length > 0) {
                    callData.selectTypeCoupon = 'CUSTOM_CATEGORY';
                    callData.customCollectionId = selectedCustomCollection.id;
                }
                else if(selectedProducts.length > 0) {
                    callData.selectTypeCoupon = 'PRODUCT';
                    callData.productList = selectedProducts.map(product => product.product.id);
                }

                const response = await createCoupon(callData);
                if(response?.status === 200) {
                    showToast('Coupon created.', 'success');
                    navigate(`${location.pathname.split('/').slice(0, -2).join('/')}`);
                }
                else {
                    showToast('Error creating coupon.', 'error');
                }
            }
            catch(err) {
                const errorMessage = err?.response?.data?.message || "Something went wrong. Please try again.";
                showToast(errorMessage, "error");
            }
        }
        else {
            setFormErrors(validationErrors);
            return;
        }
    }

    return (
        <div className="size-full overflow-y-scroll pb-16 outline-none">
            <div className="select-none flex items-center gap-2 relative m-[20px_19px_24px_20px]">
                <Link to={`${location.pathname.split('/').slice(0, -2).join('/')}`} className="p-[10px] flex items-center justify-center">
                    <img className='size-6 text-[#666666]' src={ArrowLeft} alt="" />
                </Link>

                <h1 className="p-[6px] text-[#121212] font-bold leading-8 text-[24px]">Fixed Discount Coupon</h1>
            </div>

            <div className="flex flex-col gap-6 mt-4 mx-5 w-[70%]">
                <div className="flex flex-col gap-4">
                    <CouponName />

                    <AddCouponImage />

                    <SelectCollectionOrProduct />

                    <SetFixedPrice />

                    <PurchaseRequirement />

                    <CouponLimitation />

                    <AddDuration />
                </div>

                <button onClick={handlePublishCoupon} className='w-fit ml-auto px-4 py-2 rounded bg-primaryColor hover:bg-primaryAccent font-semibold text-base text-buttonText'>Publish</button>
            </div>

            {
                modalOpen && 
                <div className={`${modalVisible ? 'opacity-100 visible bg-[#00000080]' : 'opacity-0 invisible'} transition-all duration-100 ease-in-out fixed z-50 w-full h-full top-0 left-0 flex justify-center items-center`}>
                    <CropImage />
                </div>
            }

            <Modal />
        </div>
    );
}

export default AddFixedDiscount;
